"use client";

import { ShieldCheck, Cpu, Award, Mic, CheckCircle, BarChart3, Users, Clock } from "lucide-react";
import { BPO_AGENTS } from "@/lib/demos/telecallingData";

export function TelecallingTrustSection() {
  const pillars = [
    {
      icon: Cpu,
      title: "AI Speech & Sentiment Analytics",
      description: "Every call is transcribed and scored in real-time for pitch adherence, talk-to-listen ratios, and customer sentiment signals.",
    },
    {
      icon: ShieldCheck,
      title: "Strict Regulatory Compliance",
      description: "100% compliant with TCPA, NDNC, and DND suppression registries. Automated consent logging and encrypted recording archives.",
    },
    {
      icon: Award,
      title: "Dedicated Team Lead & QA Auditor",
      description: "Weekly calibration sessions, live whisper coaching, and customized objection-handling battlecards continuously optimize conversions.",
    },
    {
      icon: BarChart3,
      title: "Real-time Telephony Synchronization",
      description: "Direct integration with Salesforce, HubSpot, Zoho, and calendar schedulers ensuring zero lead leakage.",
    },
  ];

  return (
    <section className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs font-semibold uppercase tracking-wider mb-3">
            Contact Center Architecture
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
            Institutional-Grade Telephony &amp; QA Oversight
          </h2>
          <p className="text-slate-400 text-sm sm:text-base mt-3">
            How Vocalis BPO maintains 99.8% dialing uptime and industry-leading appointment conversion rates.
          </p>
        </div>

        {/* 4 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {pillars.map((p, idx) => {
            const Icon = p.icon;
            return (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-cyan-500/30 transition-all group"
              >
                <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-5 h-5 text-cyan-400" />
                </div>
                <h3 className="font-bold text-white text-base mb-2">{p.title}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{p.description}</p>
              </div>
            );
          })}
        </div>

        {/* Agent Roster & Floor Performance Preview */}
        <div className="rounded-2xl bg-slate-900/90 border border-slate-800 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-5 mb-6">
            <div>
              <h3 className="font-bold text-white text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-400" />
                Live Dialing Floor Roster &amp; QA Benchmark
              </h3>
              <p className="text-xs text-slate-400 mt-1">
                Real-time active calling agents, daily dialed volumes, and discovery conversion metrics
              </p>
            </div>
            <span className="text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 inline-flex items-center gap-1.5 self-start sm:self-auto">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              Dialing Floor Live
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {BPO_AGENTS.map((agent) => (
              <div
                key={agent.id}
                className="p-4 rounded-xl bg-slate-950/60 border border-slate-800/80 hover:border-slate-700 transition-all"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded border border-cyan-500/20">
                    {agent.id}
                  </span>
                  <span className="text-[11px] font-bold text-amber-400">★ {agent.rating}</span>
                </div>
                <div className="font-bold text-white text-sm mt-2">{agent.name}</div>
                <div className="text-[11px] text-slate-400">{agent.role}</div>

                <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-800/60 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-500 block">Calls Today</span>
                    <span className="font-semibold text-slate-200">{agent.callsToday}</span>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-500 block">Conversion</span>
                    <span className="font-semibold text-emerald-400">{agent.conversionRate}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}
